import {
  offset,
  useFloating,
  Middleware,
  Placement,
  Strategy,
  autoUpdate,
  safePolygon,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import { ElementType, useState } from 'react';
import { PolymorphProps } from './Polymorph';
import { Portal } from './Portal';
import clsx from 'clsx';

type TooltipProps<R extends ElementType> = {
  middleware?: Middleware[];
  placement?: Placement;
  strategy?: Strategy;
  floatingElement?: React.ReactNode;
  alwaysOpen?: boolean;
  floatingClassName?: string;
  disabled?: boolean;
  forceHidden?: boolean;
} & PolymorphProps<R>;

export function Tooltip<E extends ElementType = 'div'>({
  middleware = [offset(8)],
  placement = 'top-end',
  strategy = 'fixed',
  as,
  floatingElement,
  alwaysOpen = false,
  floatingClassName,
  disabled = false,
  forceHidden = false,
  ...props
}: TooltipProps<E>) {
  const [open, setOpen] = useState(false);

  const {
    refs,
    floatingStyles,
    middlewareData,
    context: sendTooltipContext,
  } = useFloating({
    open: open,
    strategy: strategy,
    placement: placement,
    onOpenChange: setOpen,
    middleware: middleware,
    whileElementsMounted: autoUpdate,
  });
  const hover = useHover(sendTooltipContext, { handleClose: safePolygon(), enabled: !disabled });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const Reference = as || 'div';

  return (
    <>
      <Reference {...props} ref={refs.setReference} {...getReferenceProps()} />
      <Portal>
        {(alwaysOpen || open) && !forceHidden && (
          <div
            ref={refs.setFloating}
            className={clsx('z-50', floatingClassName)}
            style={{
              ...floatingStyles,
              visibility: forceHidden || middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
            }}
            {...getFloatingProps()}
          >
            {floatingElement}
          </div>
        )}
      </Portal>
    </>
  );
}
